import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { DataSearchResult } from '../enums/enums'
import { SearchService } from '../services/search.service'

export interface PaginatedSearchParams {
  query: string
  type: 'word' | 'pair' | 'dictionary' | 'user' | 'note' | 'quiz' | 'figure'
  page: number
  pageSize: number
  filters?: {
    // For words
    language?: string
    article?: string
    partOfSpeech?: string
    // For pairs
    language1?: string
    article1?: string
    partOfSpeech1?: string
    language2?: string
    article2?: string
    partOfSpeech2?: string
    // Legacy filters (still supported)
    languages?: string[]
    articles?: string[]
    partOfSpeeches?: string[]
    categories?: any[]
    tags?: any[]
  }
}

export interface PaginatedSearchResults {
  results: DataSearchResult[]
  hasMore: boolean
  totalCount: number
}

@Injectable({
  providedIn: 'root',
})
export class PaginatedSearchCommand {
  constructor(
    private searchService: SearchService,
    private logger: DdcLoggerService,
  ) {}

  /**
   * Execute paginated search based on the provided parameters
   * @param params Search parameters including query, type, page, and pageSize
   * @returns Observable with paginated search results
   */
  execute(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    this.logger.info('PaginatedSearchCommand: execute()', params)

    if (!params.query || params.query.length < 2) {
      return of({ results: [], hasMore: false, totalCount: 0 })
    }

    switch (params.type) {
      case 'word':
        return this.searchWords(params)
      case 'pair':
        return this.searchPairs(params)
      case 'dictionary':
        return this.searchDictionaries(params)
      case 'user':
        return this.searchUsers(params)
      case 'note':
        return this.searchNotes(params)
      case 'quiz':
        return this.searchQuizzes(params)
      case 'figure':
        return this.searchFigures(params)
      default:
        return of({ results: [], hasMore: false, totalCount: 0 })
    }
  }

  /**
   * Search words with pagination
   */
  private searchWords(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchWordsWithPagination(params.query, params.page, params.pageSize, params.filters)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchWords() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search pairs with pagination
   */
  private searchPairs(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchPairsWithPagination(params.query, params.page, params.pageSize, params.filters)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchPairs() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search dictionaries with pagination
   */
  private searchDictionaries(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchDictionariesWithPagination(params.query, params.page, params.pageSize)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchDictionaries() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search users with pagination
   */
  private searchUsers(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchUsersWithPagination(params.query, params.page, params.pageSize)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchUsers() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search notes with pagination
   */
  private searchNotes(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchNotesWithPagination(params.query, params.page, params.pageSize)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchNotes() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search quizzes with pagination
   */
  private searchQuizzes(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchQuizzesWithPagination(params.query, params.page, params.pageSize)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchQuizzes() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Search figures with pagination
   */
  private searchFigures(params: PaginatedSearchParams): Observable<PaginatedSearchResults> {
    return this.searchService
      .searchFiguresWithPagination(params.query, params.page, params.pageSize)
      .pipe(
        map((response: any) => {
          return {
            results: response.results,
            hasMore: response.hasMore,
            totalCount: response.totalCount,
          }
        }),
        catchError(error => {
          this.logger.error('PaginatedSearchCommand: searchFigures() error:', error)
          return of({ results: [], hasMore: false, totalCount: 0 })
        }),
      )
  }

  /**
   * Apply client-side filters to search results
   */
  private applyFilters(
    results: DataSearchResult[],
    filters?: PaginatedSearchParams['filters'],
  ): DataSearchResult[] {
    if (!filters) {
      return results
    }

    return results.filter(result => {
      // Language filter
      if (filters.languages && filters.languages.length > 0) {
        const itemLanguage = this.getItemLanguage(result)
        if (itemLanguage && !filters.languages.includes(itemLanguage)) {
          return false
        }
      }

      // Article filter
      if (filters.articles && filters.articles.length > 0) {
        const itemArticle = this.getItemArticle(result)
        if (itemArticle && !filters.articles.includes(itemArticle)) {
          return false
        }
      }

      // Part of speech filter
      if (filters.partOfSpeeches && filters.partOfSpeeches.length > 0) {
        const itemPos = this.getItemPartOfSpeech(result)
        if (itemPos && !filters.partOfSpeeches.includes(itemPos)) {
          return false
        }
      }

      // Category filter
      if (filters.categories && filters.categories.length > 0) {
        const itemCategory = this.getItemCategory(result)
        if (!itemCategory || !filters.categories.some(cat => cat.id === itemCategory.id)) {
          return false
        }
      }

      // Tag filter
      if (filters.tags && filters.tags.length > 0) {
        const itemTags = this.getItemTags(result)
        if (
          !itemTags ||
          !itemTags.some((tag: any) => filters.tags!.some(filterTag => filterTag.id === tag.id))
        ) {
          return false
        }
      }

      return true
    })
  }

  private getItemLanguage(result: DataSearchResult): string | null {
    switch (result.type) {
      case 'word':
        return result.info?.language || null
      case 'pair':
        return result.info?.language_1 || null
      default:
        return null
    }
  }

  private getItemArticle(result: DataSearchResult): string | null {
    switch (result.type) {
      case 'word':
        return result.info?.article || null
      case 'pair':
        return result.info?.article_1 || null
      default:
        return null
    }
  }

  private getItemPartOfSpeech(result: DataSearchResult): string | null {
    switch (result.type) {
      case 'word':
        return result.info?.pos || null
      case 'pair':
        return result.info?.part_of_speech_1 || null
      default:
        return null
    }
  }

  private getItemCategory(result: DataSearchResult): any {
    return result.info?.category || null
  }

  private getItemTags(result: DataSearchResult): any[] {
    return result.info?.tags || []
  }

  /**
   * Highlight search terms in results
   */
  highlightSearchTerms(results: DataSearchResult[], searchTerm: string): DataSearchResult[] {
    return results.map(result => ({
      ...result,
      label:
        result.label?.replace(
          new RegExp(searchTerm, 'gi'),
          (match: string) => `<mark>${match}</mark>`,
        ) || '',
    }))
  }
}
